// src/pages/ResourcesPage.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BookOpen,
  Heart,
  Phone,
  Users,
  Headphones,
  ExternalLink,
  AlertCircle,
  Compass,
  Globe,
  MessageCircle,
  Award,
  Sparkles,
  Shield
} from 'lucide-react';
import Card from '../components/shared/Card';
import { serviceById } from '../data/crisisSupport';

// A crisis or violence line from the fleet's one table
// (packages/crisis-support, via src/data/crisisSupport.js). The other
// services on this page are this app's own relationship and community lines.
const line = (id) => serviceById(id);

const ResourcesPage = () => {
  const [activeTab, setActiveTab] = useState('crisis');

  // Crisis lines are line('id') from the shared table; the rest are this app's own.
  const crisisSupport = {
    australia: {
      name: 'Australia',
      services: [
        line('au-1800respect'),
        line('au-lifeline'),
        {
          name: 'Relationships Australia',
          number: '1300 364 277',
          description: 'Relationship support, counselling and family dispute resolution',
          available: 'Business hours',
          website: 'relationships.org.au'
        },
        {
          name: 'MensLine Australia',
          number: '1300 78 99 78',
          description: 'Telephone and online support, information and referral service for men',
          available: '24/7',
          website: 'mensline.org.au'
        },
        {
          name: 'QLife',
          number: '1800 184 527',
          description: 'Australia-wide anonymous LGBTIQ+ peer support and referral',
          available: '3pm-9pm daily (local time)',
          website: 'qlife.org.au'
        }
      ]
    },
    newZealand: {
      name: 'New Zealand',
      services: [
        {
          name: 'Are You OK Family Violence Information Line',
          number: '0800 456 450',
          description: 'Support and information for anyone affected by family violence, or worried about someone else',
          available: '24/7',
          website: 'areyouok.org.nz'
        },
        line('nz-womens-refuge'),
        {
          name: 'Shine (Safer Homes)',
          number: '0508 744 633',
          description: 'Domestic abuse helpline',
          available: '24/7',
          website: '2shine.org.nz'
        },
        line('nz-lifeline'),
        line('nz-1737')
      ]
    },
    uk: {
      name: 'United Kingdom',
      services: [
        line('uk-national-da-helpline'),
        {
          name: 'Relate',
          number: '0300 100 1234',
          description: 'Relationship counselling and support (phone line for England)',
          available: 'Mon-Fri 9.30am-1pm',
          website: 'relate.org.uk'
        },
        line('uk-samaritans'),
        {
          name: 'Men\'s Advice Line',
          number: '0808 8010 327',
          description: 'Support for men experiencing domestic abuse',
          available: 'Weekdays (check the website for current hours)',
          website: 'mensadviceline.org.uk'
        }
      ]
    },
    ireland: {
      name: 'Ireland',
      services: [
        line('ie-womens-aid'),
        line('ie-samaritans'),
        line('ie-pieta')
      ]
    },
    canada: {
      name: 'Canada',
      services: [
        line('ca-988'),
        line('ca-hope-for-wellness'),
        line('ca-awhl'),
        {
          name: 'Ending Violence Association of Canada',
          number: 'Crisis lines vary by province or territory',
          dial: null,
          description: 'Resources and support for domestic violence',
          available: 'Varies by province',
          website: 'endingviolencecanada.org'
        }
      ]
    },
    usa: {
      name: 'United States',
      services: [
        line('us-ndvh'),
        {
          name: 'Love Is Respect',
          number: '1-866-331-9474',
          description: 'Support for young people about dating relationships',
          available: '24/7',
          website: 'loveisrespect.org'
        },
        line('us-988'),
        line('us-rainn')
      ]
    },
    india: {
      name: 'India',
      services: [
        {
          name: 'National Commission for Women Helpline',
          number: '7827-170-170',
          description: 'Support for women facing violence or distress',
          available: '24/7'
        },
        line('in-women-helpline'),
        line('in-tele-manas'),
        line('in-vandrevala')
      ]
    }
  };

  const professionalHelp = {
    whenToSeek: [
      'Communication has broken down and you can\'t resolve conflicts',
      'One or both partners are considering separation or divorce',
      'You\'re experiencing repeated patterns that damage the relationship',
      'Trust has been broken (infidelity, betrayal, deception)',
      'You feel emotionally disconnected or like flatmates',
      'Intimacy (emotional or physical) has significantly declined',
      'You\'re facing major life transitions (parenthood, career changes, loss)',
      'Mental health concerns (depression, anxiety, trauma) are affecting the relationship',
      'You want to strengthen a good relationship before problems develop'
    ],
    howToFind: [
      {
        title: 'Ask for Recommendations',
        description: 'Your GP or family doctor, trusted friends, or family members may recommend couples therapists'
      },
      {
        title: 'Check Professional Directories',
        description: 'Look for therapists registered with recognised professional bodies (see resources below)'
      },
      {
        title: 'Consider Specialisations',
        description: 'Some therapists specialise in specific approaches (EFT, Gottman Method, Imago) or issues (infidelity, blended families, LGBTQIA+ relationships)'
      },
      {
        title: 'Initial Consultation',
        description: 'Some therapists offer a short first call or session, sometimes free. Use it to assess fit and comfort'
      },
      {
        title: 'Both Partners Should Feel Comfortable',
        description: 'If either partner feels the therapist is biased or uncomfortable, try someone else'
      }
    ],
    professionalBodies: [
      {
        name: 'Relationships Australia',
        description: 'Australia-wide relationship counselling and support',
        website: 'relationships.org.au',
        country: 'Australia'
      },
      {
        name: 'Australian Counselling Association',
        description: 'Directory of qualified relationship counsellors',
        website: 'theaca.net.au',
        country: 'Australia'
      },
      {
        name: 'Psychotherapy and Counselling Federation of Australia (PACFA)',
        description: 'Register of qualified psychotherapists and counsellors',
        website: 'pacfa.org.au',
        country: 'Australia'
      },
      {
        name: 'New Zealand Association of Counsellors (NZAC)',
        description: 'Find a counsellor directory',
        website: 'nzac.org.nz',
        country: 'New Zealand'
      },
      {
        name: 'Relate UK',
        description: 'UK\'s largest provider of relationship support',
        website: 'relate.org.uk',
        country: 'United Kingdom'
      },
      {
        name: 'British Association for Counselling and Psychotherapy (BACP)',
        description: 'Professional body for therapists in the UK',
        website: 'bacp.co.uk',
        country: 'United Kingdom'
      },
      {
        name: 'Irish Association for Counselling and Psychotherapy (IACP)',
        description: 'Directory of accredited counsellors and psychotherapists',
        website: 'iacp.ie',
        country: 'Ireland'
      },
      {
        name: 'American Association for Marriage and Family Therapy (AAMFT)',
        description: 'Professional association for family therapists',
        website: 'aamft.org',
        country: 'United States'
      },
      {
        name: 'Psychology Today Therapist Directory',
        description: 'Search for couples therapists by location and specialisation',
        website: 'psychologytoday.com/us/therapists',
        country: 'United States'
      },
      {
        name: 'Canadian Counselling and Psychotherapy Association',
        description: 'National association for counsellors',
        website: 'ccpa-accp.ca',
        country: 'Canada'
      },
      {
        name: 'Rehabilitation Council of India (RCI)',
        description: 'Statutory body that registers clinical psychologists. You can ask a psychologist for their RCI registration number',
        website: 'rehabcouncil.nic.in',
        country: 'India'
      },
      {
        name: 'iCall (TISS)',
        description: 'Psychosocial counselling from the Tata Institute of Social Sciences, Monday to Saturday 8am-9pm',
        website: 'icallhelpline.org',
        country: 'India'
      }
    ],
    therapyTypes: [
      {
        name: 'Emotionally Focused Therapy (EFT)',
        description: 'Approach focussing on attachment and emotional connection, studied in clinical trials for improving relationship satisfaction.',
        bestFor: 'Emotional disconnection, conflict cycles, rebuilding after infidelity'
      },
      {
        name: 'Gottman Method',
        description: 'Approach using assessment and structured interventions, based on John and Julie Gottman\'s research with couples.',
        bestFor: 'Communication issues, conflict management, building friendship and intimacy'
      },
      {
        name: 'Imago Relationship Therapy',
        description: 'Focuses on understanding childhood wounds and how they impact adult relationships.',
        bestFor: 'Recurring patterns, deep personal growth, turning conflict into connection'
      },
      {
        name: 'Cognitive Behavioural Couples Therapy (CBCT)',
        description: 'Addresses problematic thoughts and behaviours affecting the relationship.',
        bestFor: 'Changing specific problematic patterns, managing mental health in relationships'
      },
      {
        name: 'Discernment Counselling',
        description: 'Short-term therapy (1-5 sessions) for couples considering separation.',
        bestFor: 'When one or both partners are unsure about staying together'
      }
    ]
  };

  const books = {
    foundational: [
      {
        title: 'The Seven Principles for Making Marriage Work',
        author: 'John Gottman & Nan Silver',
        year: '2015 (Revised)',
        description: 'A practical guide to Gottman\'s seven principles, drawn from his research with couples. Includes questionnaires and exercises.',
        rating: 5,
        topics: ['Communication', 'Conflict', 'Connection', 'Research-Based']
      },
      {
        title: 'Hold Me Tight: Seven Conversations for a Lifetime of Love',
        author: 'Sue Johnson',
        year: '2008',
        description: 'Sue Johnson\'s guide for couples, based on Emotionally Focused Therapy (EFT). Explains attachment in relationships and offers seven conversations to deepen connection.',
        rating: 5,
        topics: ['Emotional Connection', 'Attachment', 'Intimacy', 'Conflict']
      },
      {
        title: 'Eight Dates: Essential Conversations for a Lifetime of Love',
        author: 'John Gottman, Julie Schwartz Gottman, Doug Abrams & Rachel Carlton Abrams',
        year: '2019',
        description: 'Eight structured date conversations covering trust, conflict, sex, money, family, adventure, spirituality, and dreams.',
        rating: 5,
        topics: ['Connection', 'Deep Conversations', 'Intimacy', 'Long-Term Planning']
      }
    ],
    communication: [
      {
        title: 'Difficult Conversations: How to Discuss What Matters Most',
        author: 'Douglas Stone, Bruce Patton & Sheila Heen',
        year: '2010',
        description: 'A framework from the Harvard Negotiation Project for navigating challenging conversations. Written for all kinds of conversations, and useful for couples.',
        rating: 5,
        topics: ['Communication', 'Conflict Resolution', 'Difficult Topics']
      },
      {
        title: 'Nonviolent Communication: A Language of Life',
        author: 'Marshall Rosenberg',
        year: '2015 (3rd Edition)',
        description: 'A framework for expressing needs and feelings without blame, aimed at reducing defensiveness.',
        rating: 4,
        topics: ['Communication', 'Empathy', 'Needs']
      },
      {
        title: 'The Lost Art of Listening: How Learning to Listen Can Improve Relationships',
        author: 'Michael P. Nichols & Martha B. Straus',
        year: '2021 (3rd Edition)',
        description: 'A guide to active listening and empathetic communication. Explains why listening is so hard and how to get better at it.',
        rating: 4,
        topics: ['Listening', 'Communication', 'Empathy']
      },
      {
        title: 'Why Won\'t You Apologize? Healing Big Betrayals and Everyday Hurts',
        author: 'Harriet Lerner',
        year: '2017',
        description: 'On genuine apology and repair, including why some people find apologising so hard.',
        rating: 5,
        topics: ['Repair', 'Apology', 'Accountability', 'Trust']
      }
    ],
    conflict: [
      {
        title: 'Fighting for Your Marriage: A Deluxe Revised Edition',
        author: 'Howard Markman, Scott Stanley & Susan Blumberg',
        year: '2010',
        description: 'The Prevention and Relationship Enhancement Program (PREP) and research-based conflict resolution strategies. Practical exercises included.',
        rating: 4,
        topics: ['Conflict', 'Communication', 'Prevention']
      },
      {
        title: 'After the Honeymoon: How Conflict Can Improve Your Relationship',
        author: 'Dan Wile',
        year: '2008 (Revised)',
        description: 'Understanding and managing perpetual problems. Refreshingly realistic about conflict in relationships.',
        rating: 4,
        topics: ['Conflict', 'Perpetual Problems', 'Acceptance']
      },
      {
        title: 'Us: Getting Past You and Me to Build a More Loving Relationship',
        author: 'Terrence Real',
        year: '2022',
        description: 'Relational Life Therapy techniques for fair fighting and repair. Direct and practical.',
        rating: 5,
        topics: ['Conflict', 'Repair', 'Communication', 'Boundaries']
      }
    ],
    intimacy: [
      {
        title: 'Mating in Captivity: Reconciling the Erotic and the Domestic',
        author: 'Esther Perel',
        year: '2006',
        description: 'Navigating desire, novelty, and sustaining physical intimacy in long-term relationships.',
        rating: 5,
        topics: ['Sexual Intimacy', 'Desire', 'Novelty', 'Long-Term Relationships']
      },
      {
        title: 'Come As You Are: The Surprising New Science That Will Transform Your Sex Life',
        author: 'Emily Nagoski',
        year: '2021 (Revised)',
        description: 'Understanding sexual desire, responsive desire model, and navigating physical intimacy. Science-based and compassionate.',
        rating: 5,
        topics: ['Sexual Desire', 'Women\'s Sexuality', 'Responsive Desire']
      },
      {
        title: 'Daring Greatly: How the Courage to Be Vulnerable Transforms the Way We Live, Love, Parent, and Lead',
        author: 'Brené Brown',
        year: '2012',
        description: 'The role of vulnerability in relationships and connection. Not specifically about couples, but relevant to them.',
        rating: 5,
        topics: ['Vulnerability', 'Shame', 'Connection', 'Courage']
      }
    ],
    trust: [
      {
        title: 'What Makes Love Last? How to Build Trust and Avoid Betrayal',
        author: 'John Gottman & Nan Silver',
        year: '2012',
        description: 'How trust is built and how betrayal erodes it, with Gottman\'s ATTUNE model for building trust and guidance on recovering after betrayal.',
        rating: 5,
        topics: ['Trust', 'Betrayal', 'Infidelity', 'Repair']
      },
      {
        title: 'After the Affair: Healing the Pain and Rebuilding Trust',
        author: 'Janis Abrahms Spring',
        year: '2012 (Updated)',
        description: 'A guide to trust repair after infidelity, for both hurt and unfaithful partners.',
        rating: 5,
        topics: ['Infidelity', 'Trust Repair', 'Forgiveness', 'Rebuilding']
      },
      {
        title: 'The Science of Trust: Emotional Attunement for Couples',
        author: 'John Gottman',
        year: '2011',
        description: 'Research on emotional attunement and its role in relationship security. More academic than his other books.',
        rating: 4,
        topics: ['Trust', 'Attunement', 'Research', 'Security']
      }
    ],
    growth: [
      {
        title: 'Passionate Marriage: Keeping Love and Intimacy Alive',
        author: 'David Schnarch',
        year: '2009',
        description: 'Differentiation and intimacy in long-term relationships. A demanding read.',
        rating: 4,
        topics: ['Differentiation', 'Sexual Intimacy', 'Personal Growth']
      },
      {
        title: 'The All-or-Nothing Marriage: How the Best Marriages Work',
        author: 'Eli Finkel',
        year: '2017',
        description: 'Finkel\'s argument that expectations of marriage have changed over time (his "suffocation model"), and what that means for couples today.',
        rating: 5,
        topics: ['Modern Marriage', 'Expectations', 'Growth', 'Research']
      },
      {
        title: 'Wired for Love: How Understanding Your Partner\'s Brain Can Help You Defuse Conflicts',
        author: 'Stan Tatkin',
        year: '2012',
        description: 'Draws on attachment theory and neuroscience, with practical exercises for couples.',
        rating: 4,
        topics: ['Neuroscience', 'Attachment', 'Conflict', 'Security']
      }
    ],
    emotional: [
      {
        title: 'Emotional Agility: Get Unstuck, Embrace Change, and Thrive',
        author: 'Susan David',
        year: '2016',
        description: 'Naming emotions precisely and facing all of them without judgement, so they can guide rather than drive you.',
        rating: 5,
        topics: ['Emotional Intelligence', 'Self-Awareness', 'Psychological Flexibility']
      },
      {
        title: 'How Emotions Are Made: The Secret Life of the Brain',
        author: 'Lisa Feldman Barrett',
        year: '2017',
        description: 'Barrett\'s theory of constructed emotion, and why a richer emotional vocabulary matters.',
        rating: 4,
        topics: ['Neuroscience', 'Emotions', 'Emotional Vocabulary']
      },
      {
        title: 'Permission to Feel: Unlocking the Power of Emotions',
        author: 'Marc Brackett',
        year: '2019',
        description: 'Building emotional vocabulary and emotional intelligence skills. Practical and research-based.',
        rating: 4,
        topics: ['Emotional Intelligence', 'Emotional Vocabulary', 'Regulation']
      },
      {
        title: 'Emotional Intelligence: Why It Can Matter More Than IQ',
        author: 'Daniel Goleman',
        year: '2005',
        description: 'The book that popularised emotional intelligence and describes its components.',
        rating: 4,
        topics: ['Emotional Intelligence', 'Self-Awareness', 'Relationships']
      }
    ]
  };

  const digitalResources = [
    {
      name: 'Gottman Card Decks App',
      type: 'App',
      description: 'Digital versions of Gottman\'s conversation card decks for building connection',
      website: 'gottman.com/couples/apps'
    },
    {
      name: 'Where Should We Begin? Podcast',
      type: 'Podcast',
      description: 'Esther Perel\'s acclaimed podcast featuring real couples therapy sessions',
      website: 'estherperel.com/podcast'
    },
    {
      name: 'The Gottman Institute Podcast',
      type: 'Podcast',
      description: 'Relationship research and practical advice from the Gottman Institute',
      website: 'gottman.com/blog/category/podcast'
    },
    {
      name: 'Relationship Theory Podcast',
      type: 'Podcast',
      description: 'Tom and Lisa Bilyeu answer relationship questions and talk about their own marriage',
      website: 'impacttheory.com'
    },
    {
      name: 'The Love, Happiness & Success Podcast',
      type: 'Podcast',
      description: 'Dr. Lisa Marie Bobby\'s podcast covering relationships and personal growth',
      website: 'growingself.com/podcast'
    },
    {
      name: 'Coupled App',
      type: 'App',
      description: 'Daily questions and quizzes for couples to deepen connection',
      website: 'coupled.app'
    },
    {
      name: 'Lasting App',
      type: 'App',
      description: 'Marriage counselling app based on relationship science',
      website: 'getlasting.com'
    }
  ];

  const tabs = [
    { id: 'crisis', name: 'Crisis Support', icon: Phone },
    { id: 'professional', name: 'Professional Help', icon: Users },
    { id: 'books', name: 'Books & Research', icon: BookOpen },
    { id: 'digital', name: 'Digital Resources', icon: Headphones }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Hero Section */}
      <div className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-rose-600/10 via-pink-600/10 to-purple-600/10" />

        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            {/* Hero Image */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="mb-6 inline-block"
            >
              <img
                src="/images/resources.png"
                alt="Resources"
                className="w-60 h-60 md:w-80 md:h-80 object-contain rounded-2xl mx-auto"
              />
            </motion.div>

            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white via-rose-100 to-pink-100 bg-clip-text text-transparent">
              Relationship Resources & Support
            </h1>

            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Books, professional support services, and crisis resources to help you navigate your relationship
            </p>
          </motion.div>
        </div>
      </div>

      {/* Tabs */}
      <div className="container mx-auto px-4 mb-8">
        <div className="flex flex-wrap justify-center gap-2 md:gap-4">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                aria-pressed={activeTab === tab.id}
                className={`flex items-center gap-2 px-3 md:px-6 py-3 rounded-lg font-semibold transition-all ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-lg scale-105'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                }`}
              >
                <Icon className="w-5 h-5" aria-hidden="true" />
                <span className="text-sm md:text-base">{tab.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content Sections */}
      <div className="container mx-auto px-4 pb-20">
        {/* Crisis Support Tab */}
        {activeTab === 'crisis' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-6xl mx-auto"
          >
            {/* Emergency Warning */}
            <Card className="mb-8 border-2 border-red-500/50 bg-gradient-to-br from-red-500/10 to-rose-500/10">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">If You're in Immediate Danger</h3>
                  <p className="text-slate-300 mb-4">
                    If you're in immediate danger, call your local emergency number now (000 in Australia, 111 in New Zealand, 112 in India, 999 or 112 in the UK and Ireland, 911 in Canada and the US). If you're somewhere else and not sure of the number, your phone's emergency dialler lists it.
                  </p>
                  <p className="text-slate-400 text-sm">
                    The services below offer confidential support for relationship distress, domestic violence, and emotional crises. Hours vary, so check each service's availability.
                  </p>
                </div>
              </div>
            </Card>

            {/* Crisis Support by Country */}
            {Object.entries(crisisSupport).map(([key, country]) => (
              <Card key={key} className="mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <Globe className="w-6 h-6 text-rose-400" />
                  <h3 className="text-xl sm:text-2xl font-bold text-white">{country.name}</h3>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {country.services.map((service, idx) => {
                    // Dial only the digits (and a leading +). An entry with no
                    // number to dial (dial: null) is shown as plain text, never
                    // as a broken tel: link.
                    const dial = service.dial === undefined
                      ? service.number.replace(/[^\d+]/g, '')
                      : service.dial;
                    return (
                    <div
                      key={idx}
                      className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-rose-500/50 transition-colors"
                    >
                      <h4 className="text-lg font-bold text-white mb-2">{service.name}</h4>
                      <div className="flex items-center gap-2 mb-2">
                        <Phone className="w-4 h-4 text-rose-400" />
                        {dial && /\d/.test(dial) ? (
                          <a
                            href={`tel:${dial}`}
                            className="text-rose-400 hover:text-rose-300 font-semibold text-lg"
                          >
                            {service.number}
                          </a>
                        ) : (
                          <span className="text-slate-300 font-semibold">{service.number}</span>
                        )}
                      </div>
                      <p className="text-slate-300 text-sm mb-2">{service.description}</p>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-400">{service.available ? `Available: ${service.available}` : ''}</span>
                        {service.website && (
                          <a
                            href={`https://${service.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-rose-400 hover:text-rose-300 flex items-center gap-1"
                          >
                            <span>{service.website}</span>
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </Card>
            ))}
          </motion.div>
        )}

        {/* Professional Help Tab */}
        {activeTab === 'professional' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-6xl mx-auto space-y-8"
          >
            {/* When to Seek Help */}
            <Card>
              <div className="flex items-center gap-3 mb-6">
                <Heart className="w-6 h-6 text-rose-400" />
                <h3 className="text-xl sm:text-2xl font-bold text-white">When to Seek Professional Help</h3>
              </div>

              <p className="text-slate-300 mb-6">
                Seeking couples therapy is a sign of strength, not weakness. Consider professional support if:
              </p>

              <ul className="grid md:grid-cols-2 gap-3">
                {professionalHelp.whenToSeek.map((reason, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-slate-300">
                    <div className="w-2 h-2 bg-rose-400 rounded-full mt-2 flex-shrink-0" />
                    <span>{reason}</span>
                  </li>
                ))}
              </ul>
            </Card>

            {/* How to Find a Therapist */}
            <Card>
              <div className="flex items-center gap-3 mb-6">
                <Compass className="w-6 h-6 text-rose-400" />
                <h3 className="text-xl sm:text-2xl font-bold text-white">How to Find a Couples Therapist</h3>
              </div>

              <div className="space-y-4">
                {professionalHelp.howToFind.map((step, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="w-8 h-8 bg-gradient-to-br from-rose-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold">{idx + 1}</span>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-1">{step.title}</h4>
                      <p className="text-slate-300">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Professional Bodies and Directories */}
            <Card>
              <div className="flex items-center gap-3 mb-6">
                <Users className="w-6 h-6 text-rose-400" />
                <h3 className="text-xl sm:text-2xl font-bold text-white">Professional Bodies & Directories</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {professionalHelp.professionalBodies.map((body, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-rose-500/50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-white font-semibold flex-1">{body.name}</h4>
                      <span className="text-xs text-slate-400 px-2 py-1 bg-slate-700 rounded">
                        {body.country}
                      </span>
                    </div>
                    <p className="text-slate-300 text-sm mb-3">{body.description}</p>
                    <a
                      href={`https://${body.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-rose-400 hover:text-rose-300 text-sm flex items-center gap-1"
                    >
                      <span>{body.website}</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                ))}
              </div>
            </Card>

            {/* Types of Therapy */}
            <Card>
              <div className="flex items-center gap-3 mb-6">
                <MessageCircle className="w-6 h-6 text-rose-400" />
                <h3 className="text-xl sm:text-2xl font-bold text-white">Types of Couples Therapy</h3>
              </div>

              <div className="space-y-4">
                {professionalHelp.therapyTypes.map((therapy, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-slate-800/50 rounded-lg border border-slate-700"
                  >
                    <h4 className="text-lg font-semibold text-white mb-2">{therapy.name}</h4>
                    <p className="text-slate-300 mb-3">{therapy.description}</p>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-slate-400">Best for:</span>
                      <span className="text-rose-400">{therapy.bestFor}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>
        )}

        {/* Books & Research Tab */}
        {activeTab === 'books' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-6xl mx-auto space-y-8"
          >
            <Card>
              <p className="text-slate-300 mb-6">
                A reading list on relationships and emotional skills, organised by topic.
              </p>
            </Card>

            {/* Foundational Books */}
            <Card>
              <div className="flex items-center gap-3 mb-6">
                <Award className="w-6 h-6 text-rose-400" />
                <h3 className="text-xl sm:text-2xl font-bold text-white">Essential Foundational Books</h3>
              </div>
              <p className="text-slate-400 mb-6 text-sm">Start here: three practical books by couples researchers and therapists</p>

              <div className="grid md:grid-cols-1 gap-4">
                {books.foundational.map((book, idx) => (
                  <div
                    key={idx}
                    className="p-5 bg-gradient-to-br from-slate-800/80 to-slate-800/40 rounded-lg border-2 border-rose-500/30 hover:border-rose-500/60 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-white mb-1">{book.title}</h4>
                        <p className="text-rose-400 text-sm mb-2">{book.author} • {book.year}</p>
                      </div>
                      <div className="flex gap-0.5">
                        {[...Array(book.rating)].map((_, i) => (
                          <Sparkles key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        ))}
                      </div>
                    </div>
                    <p className="text-slate-300 mb-3">{book.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {book.topics.map((topic, topicIdx) => (
                        <span
                          key={topicIdx}
                          className="px-2 py-1 bg-rose-500/20 text-rose-300 text-xs rounded"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Communication Books */}
            <Card>
              <div className="flex items-center gap-3 mb-6">
                <MessageCircle className="w-6 h-6 text-rose-400" />
                <h3 className="text-xl sm:text-2xl font-bold text-white">Communication & Repair</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {books.communication.map((book, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-rose-500/50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-white font-semibold flex-1 text-base">{book.title}</h4>
                      <div className="flex gap-0.5">
                        {[...Array(book.rating)].map((_, i) => (
                          <Sparkles key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                        ))}
                      </div>
                    </div>
                    <p className="text-rose-400 text-xs mb-2">{book.author} • {book.year}</p>
                    <p className="text-slate-300 text-sm mb-3">{book.description}</p>
                    <div className="flex flex-wrap gap-1">
                      {book.topics.map((topic, topicIdx) => (
                        <span
                          key={topicIdx}
                          className="px-2 py-0.5 bg-slate-700 text-slate-300 text-xs rounded"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Conflict Books */}
            <Card>
              <div className="flex items-center gap-3 mb-6">
                <Shield className="w-6 h-6 text-rose-400" />
                <h3 className="text-xl sm:text-2xl font-bold text-white">Conflict & Resolution</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {books.conflict.map((book, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-rose-500/50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-white font-semibold flex-1 text-base">{book.title}</h4>
                      <div className="flex gap-0.5">
                        {[...Array(book.rating)].map((_, i) => (
                          <Sparkles key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                        ))}
                      </div>
                    </div>
                    <p className="text-rose-400 text-xs mb-2">{book.author} • {book.year}</p>
                    <p className="text-slate-300 text-sm mb-3">{book.description}</p>
                    <div className="flex flex-wrap gap-1">
                      {book.topics.map((topic, topicIdx) => (
                        <span
                          key={topicIdx}
                          className="px-2 py-0.5 bg-slate-700 text-slate-300 text-xs rounded"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Intimacy Books */}
            <Card>
              <div className="flex items-center gap-3 mb-6">
                <Heart className="w-6 h-6 text-rose-400" />
                <h3 className="text-xl sm:text-2xl font-bold text-white">Intimacy & Vulnerability</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {books.intimacy.map((book, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-rose-500/50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-white font-semibold flex-1 text-base">{book.title}</h4>
                      <div className="flex gap-0.5">
                        {[...Array(book.rating)].map((_, i) => (
                          <Sparkles key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                        ))}
                      </div>
                    </div>
                    <p className="text-rose-400 text-xs mb-2">{book.author} • {book.year}</p>
                    <p className="text-slate-300 text-sm mb-3">{book.description}</p>
                    <div className="flex flex-wrap gap-1">
                      {book.topics.map((topic, topicIdx) => (
                        <span
                          key={topicIdx}
                          className="px-2 py-0.5 bg-slate-700 text-slate-300 text-xs rounded"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Trust Books */}
            <Card>
              <div className="flex items-center gap-3 mb-6">
                <Shield className="w-6 h-6 text-rose-400" />
                <h3 className="text-xl sm:text-2xl font-bold text-white">Trust & Betrayal</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {books.trust.map((book, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-rose-500/50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-white font-semibold flex-1 text-base">{book.title}</h4>
                      <div className="flex gap-0.5">
                        {[...Array(book.rating)].map((_, i) => (
                          <Sparkles key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                        ))}
                      </div>
                    </div>
                    <p className="text-rose-400 text-xs mb-2">{book.author} • {book.year}</p>
                    <p className="text-slate-300 text-sm mb-3">{book.description}</p>
                    <div className="flex flex-wrap gap-1">
                      {book.topics.map((topic, topicIdx) => (
                        <span
                          key={topicIdx}
                          className="px-2 py-0.5 bg-slate-700 text-slate-300 text-xs rounded"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Growth Books */}
            <Card>
              <div className="flex items-center gap-3 mb-6">
                <Compass className="w-6 h-6 text-rose-400" />
                <h3 className="text-xl sm:text-2xl font-bold text-white">Growth & Long-Term Thriving</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {books.growth.map((book, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-rose-500/50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-white font-semibold flex-1 text-base">{book.title}</h4>
                      <div className="flex gap-0.5">
                        {[...Array(book.rating)].map((_, i) => (
                          <Sparkles key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                        ))}
                      </div>
                    </div>
                    <p className="text-rose-400 text-xs mb-2">{book.author} • {book.year}</p>
                    <p className="text-slate-300 text-sm mb-3">{book.description}</p>
                    <div className="flex flex-wrap gap-1">
                      {book.topics.map((topic, topicIdx) => (
                        <span
                          key={topicIdx}
                          className="px-2 py-0.5 bg-slate-700 text-slate-300 text-xs rounded"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Emotional Intelligence Books */}
            <Card>
              <div className="flex items-center gap-3 mb-6">
                <Sparkles className="w-6 h-6 text-rose-400" />
                <h3 className="text-xl sm:text-2xl font-bold text-white">Emotional Intelligence</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {books.emotional.map((book, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-rose-500/50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-white font-semibold flex-1 text-base">{book.title}</h4>
                      <div className="flex gap-0.5">
                        {[...Array(book.rating)].map((_, i) => (
                          <Sparkles key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                        ))}
                      </div>
                    </div>
                    <p className="text-rose-400 text-xs mb-2">{book.author} • {book.year}</p>
                    <p className="text-slate-300 text-sm mb-3">{book.description}</p>
                    <div className="flex flex-wrap gap-1">
                      {book.topics.map((topic, topicIdx) => (
                        <span
                          key={topicIdx}
                          className="px-2 py-0.5 bg-slate-700 text-slate-300 text-xs rounded"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>
        )}

        {/* Digital Resources Tab */}
        {activeTab === 'digital' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-6xl mx-auto"
          >
            <Card>
              <div className="flex items-center gap-3 mb-6">
                <Headphones className="w-6 h-6 text-rose-400" />
                <h3 className="text-xl sm:text-2xl font-bold text-white">Apps, Podcasts & Online Resources</h3>
              </div>

              <p className="text-slate-300 mb-6">
                Supplement your learning with these high-quality digital resources
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                {digitalResources.map((resource, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-rose-500/50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-lg font-semibold text-white flex-1">{resource.name}</h4>
                      <span className="px-2 py-1 bg-rose-500/20 text-rose-300 text-xs rounded">
                        {resource.type}
                      </span>
                    </div>
                    <p className="text-slate-300 text-sm mb-3">{resource.description}</p>
                    <a
                      href={`https://${resource.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-rose-400 hover:text-rose-300 text-sm flex items-center gap-1"
                    >
                      <span>{resource.website}</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ResourcesPage;
